/**
 * Common validation functions for condition fields
 */

const PATH_ONLY_ERROR = 'Enter only a page path beginning with /, without a domain, query string, or hash.'

type PathInputOptions = {
  strict?: boolean
}

const stripQueryAndHash = (value: string): string => {
  return value.split('#')[0].split('?')[0]
}

const isExplicitUrl = (value: string): boolean => /^https?:\/\//i.test(value)

const isDomainLikeValue = (value: string): boolean =>
  /^[^/\s?#]+\.[^/\s?#]+(?:[/?#].*)?$/.test(value)

/**
 * Normalizes pageview path inputs. Default mode preserves legacy runtime-supported
 * values; strict mode coerces URL-like input to pathname-only for AI-generated
 * conditions.
 */
export const normalizePathInput = (
  value: string,
  options: PathInputOptions = {}
): { value: string; wasFullUrl: boolean } => {
  const trimmed = value.trim()

  if (!trimmed) {
    return { value: '', wasFullUrl: false }
  }

  if (options.strict) {
    if (isExplicitUrl(trimmed)) {
      try {
        const parsed = new URL(trimmed)
        return { value: parsed.pathname || '/', wasFullUrl: true }
      } catch {
        return { value: trimmed, wasFullUrl: true }
      }
    }

    if (!trimmed.startsWith('/') && isDomainLikeValue(trimmed)) {
      try {
        const parsed = new URL(`https://${trimmed}`)
        return { value: parsed.pathname || '/', wasFullUrl: false }
      } catch {
        // Fall through to bare-path normalization below.
      }
    }

    const normalizedPath = stripQueryAndHash(trimmed)

    if (!normalizedPath) {
      return { value: '/', wasFullUrl: false }
    }

    return {
      value: normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`,
      wasFullUrl: false
    }
  }

  if (trimmed.startsWith('/') || isExplicitUrl(trimmed) || isDomainLikeValue(trimmed)) {
    return { value: trimmed, wasFullUrl: false }
  }

  return {
    value: `/${trimmed}`,
    wasFullUrl: false
  }
}

/**
 * Validates pageview path values. Default mode preserves legacy runtime-supported
 * values; strict mode enforces pathname-only authoring.
 */
export const validatePath = (value: string, options: PathInputOptions = {}): string | null => {
  if (!value) return null

  const trimmed = value.trim()

  if (!trimmed) return null

  if (options.strict) {
    if (!trimmed.startsWith('/') || trimmed.includes('?') || trimmed.includes('#')) {
      return PATH_ONLY_ERROR
    }

    return null
  }

  if (isExplicitUrl(trimmed)) {
    try {
      new URL(trimmed)
      return null
    } catch {
      return PATH_ONLY_ERROR
    }
  }

  if (trimmed.includes(' ')) {
    return PATH_ONLY_ERROR
  }

  return null
}

/**
 * Validates that a value is a valid domain (without protocol)
 * @param value The value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateDomain = (value: string): string | null => {
  if (!value) return null // Allow empty values
  
  // Check for protocol
  if (value.match(/^https?:\/\//i) || value.includes('://')) {
    return 'Please enter only the domain (e.g., example.com), not the full URL'
  }
  
  // Check for paths
  if (value.includes('/') && value !== '/') {
    return 'Please enter only the domain, not the path'
  }
  
  // Check for TLD (must have at least one period)
  if (!value.includes('.')) {
    return 'Please enter a valid domain with a TLD (e.g., example.com)'
  }
  
  return null
}

/**
 * Validates that a number is within a specific range
 * @param min Minimum value (inclusive)
 * @param max Maximum value (inclusive)
 * @returns Validation function
 */
export const validateNumberRange = (min: number, max: number) => {
  return (value: number): string | null => {
    if (value === null || value === undefined) return null
    
    if (value < min || value > max) {
      return `Value must be between ${min} and ${max}`
    }
    
    return null
  }
}

/**
 * Validates that a string matches a specific pattern
 * @param pattern Regular expression pattern
 * @param errorMessage Error message to show when validation fails
 * @returns Validation function
 */
export const validatePattern = (pattern: RegExp, errorMessage: string) => {
  return (value: string): string | null => {
    if (!value) return null
    
    if (!pattern.test(value)) {
      return errorMessage
    }
    
    return null
  }
}

/**
 * Validates that a value is a valid URL
 * @param value The value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateURL = (value: string): string | null => {
  if (!value) return null // Allow empty values
  
  try {
    const url = new URL(value)
    // Ensure it's http or https
    if (!['http:', 'https:'].includes(url.protocol)) {
      return 'Please enter a valid URL starting with http:// or https://'
    }
    return null
  } catch {
    return 'Please enter a valid URL (e.g., https://example.com/page)'
  }
}

/**
 * Combines multiple validators
 * @param validators Array of validation functions
 * @returns Combined validation function that returns the first error found
 */
export const combineValidators = (...validators: Array<(value: any) => string | null>) => {
  return (value: any): string | null => {
    for (const validator of validators) {
      const error = validator(value)
      if (error) return error
    }
    return null
  }
}